import React, { useState, useEffect, useRef, useCallback } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api, { getImageUrl, PLACEHOLDER_IMAGE } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Send, ShieldAlert, MessageCircle, Search, Loader2, ChevronUp, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

export default function Conversations() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [wsConnected, setWsConnected] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const wsRef = useRef(null);
  const msgsEndRef = useRef(null);
  const msgsContainerRef = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timer: null, manual: false });
  const activeConvIdRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  const PAGE_SIZE = 30;

  const loadConversations = useCallback(async () => {
    try {
      const r = await api.get('/admin/conversations');
      setConversations(r.data);
    } catch {
      toast.error('Failed to load conversations');
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const fetchLatest = useCallback(async (convId) => {
    try {
      const r = await api.get(`/admin/conversations/${convId}/messages`, { params: { limit: PAGE_SIZE } });
      const incoming = r.data.messages || [];
      setMessages(prev => {
        const byId = new Map(prev.map(m => [m.id, m]));
        incoming.forEach(m => byId.set(m.id, m));
        return Array.from(byId.values()).sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
      });
    } catch { /* ignore */ }
  }, []);

  const connectWS = useCallback((convId) => {
    const token = localStorage.getItem('tlr_token');
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || '')
      .replace(/^https:/, 'wss:')
      .replace(/^http:/, 'ws:');
    const wsUrl = `${base}/api/ws/conversations/${convId}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => {
      setWsConnected(true);
      setReconnecting(false);
      if (reconnectRef.current.attempts > 0) fetchLatest(convId);
      reconnectRef.current.attempts = 0;
    };
    ws.onclose = () => {
      setWsConnected(false);
      wsRef.current = null;
      if (!reconnectRef.current.manual && activeConvIdRef.current === convId) {
        const attempt = reconnectRef.current.attempts + 1;
        if (attempt <= 6) {
          reconnectRef.current.attempts = attempt;
          const delay = Math.min(1000 * 2 ** (attempt - 1), 15000);
          setReconnecting(true);
          clearTimeout(reconnectRef.current.timer);
          reconnectRef.current.timer = setTimeout(() => {
            if (activeConvIdRef.current === convId) connectWS(convId);
          }, delay);
        } else {
          setReconnecting(false);
        }
      }
    };
    ws.onerror = () => {};
    ws.onmessage = (e) => {
      try {
        const { type, data } = JSON.parse(e.data);
        if (type === 'message') {
          setMessages(prev => prev.find(m => m.id === data.id) ? prev : [...prev, data]);
        } else if (type === 'error') {
          toast.error(data.message);
        }
      } catch {}
    };
  }, [fetchLatest]);

  const openConversation = useCallback(async (conv) => {
    reconnectRef.current.manual = true;
    clearTimeout(reconnectRef.current.timer);
    reconnectRef.current.attempts = 0;
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    setReconnecting(false);
    activeConvIdRef.current = conv.id;
    setActiveConv(conv);
    setMessages([]);
    setHasMore(false);
    lastMsgIdRef.current = null;
    setLoadingMsgs(true);
    try {
      const r = await api.get(`/admin/conversations/${conv.id}/messages`, { params: { limit: PAGE_SIZE } });
      setMessages(r.data.messages || []);
      setHasMore(!!r.data.has_more);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoadingMsgs(false);
    }
    reconnectRef.current.manual = false;
    connectWS(conv.id);
  }, [connectWS]);

  const loadOlder = async () => {
    if (!activeConv || loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    const container = msgsContainerRef.current;
    const prevHeight = container ? container.scrollHeight : 0;
    try {
      const before = messages[0].timestamp;
      const r = await api.get(`/admin/conversations/${activeConv.id}/messages`, { params: { before, limit: PAGE_SIZE } });
      const older = r.data.messages || [];
      setMessages(prev => {
        const existing = new Set(prev.map(m => m.id));
        return [...older.filter(m => !existing.has(m.id)), ...prev];
      });
      setHasMore(!!r.data.has_more);
      requestAnimationFrame(() => {
        if (container) container.scrollTop = container.scrollHeight - prevHeight;
      });
    } catch {
      toast.error('Failed to load older messages');
    } finally {
      setLoadingOlder(false);
    }
  };

  useEffect(() => () => {
    reconnectRef.current.manual = true;
    clearTimeout(reconnectRef.current.timer);
    if (wsRef.current) wsRef.current.close();
  }, []);

  useEffect(() => {
    const lastId = messages[messages.length - 1]?.id;
    if (lastId && lastId !== lastMsgIdRef.current) {
      lastMsgIdRef.current = lastId;
      msgsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendAdminMessage = async () => {
    const content = draft.trim();
    if (!content || !activeConv) return;
    setSending(true);
    setDraft('');
    try {
      const r = await api.post(`/admin/conversations/${activeConv.id}/messages`, { content });
      setMessages(prev => [...prev, r.data]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
      setDraft(content);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAdminMessage(); }
  };

  const filtered = conversations.filter(c =>
    !search ||
    c.buyer_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.seller_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.listing_title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardShell title="Conversations">
      <div
        className="flex rounded-2xl overflow-hidden border"
        style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)', height: 'calc(100vh - 160px)', minHeight: '520px' }}
        data-testid="admin-conversations-container"
      >
        {/* Left: list */}
        <div className="flex-shrink-0 border-r flex flex-col" style={{ width: '320px', borderColor: 'var(--border-clr)', backgroundColor: 'var(--bg)' }}>
          <div className="px-4 py-4 border-b space-y-3" style={{ borderColor: 'var(--border-clr)' }}>
            <div>
              <h2 className="font-semibold text-base" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>All Conversations</h2>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{conversations.length} total</p>
            </div>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search conversations…"
                className="pl-8 text-xs"
                style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingConvs ? (
              <div className="p-4 space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <MessageCircle size={28} className="mb-3" style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No conversations found</p>
              </div>
            ) : filtered.map(conv => {
              const isActive = activeConv?.id === conv.id;
              const thumb = conv.listing_image ? getImageUrl(conv.listing_image) : PLACEHOLDER_IMAGE;
              return (
                <button
                  key={conv.id}
                  onClick={() => openConversation(conv)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 transition-colors hover:bg-[var(--bg-subtle)]"
                  style={{ backgroundColor: isActive ? 'var(--bg-subtle)' : 'transparent', borderLeft: isActive ? '3px solid var(--gold)' : '3px solid transparent' }}
                >
                  <img src={thumb} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" style={{ backgroundColor: 'var(--bg-subtle)' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>{conv.buyer_name} ↔ {conv.seller_name}</p>
                    <p className="text-xs truncate mt-0.5 italic" style={{ color: 'var(--text-muted)' }}>{conv.listing_title}</p>
                    <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-muted)', opacity: 0.7 }}>{conv.last_message || '…'}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: thread */}
        <div className="flex-1 flex flex-col min-w-0">
          {!activeConv ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <ShieldAlert size={40} className="mb-4" style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
              <p className="text-lg font-medium" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Select a conversation</p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>View and intervene in any member conversation</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: 'var(--border-clr)' }}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{activeConv.buyer_name} ↔ {activeConv.seller_name}</p>
                    {reconnecting ? (
                      <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--warning)' }} data-testid="admin-ws-reconnecting">
                        <RefreshCw size={10} className="animate-spin" /> Reconnecting…
                      </span>
                    ) : wsConnected ? (
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--success)' }} title="Live" />
                    ) : null}
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Re: <em>{activeConv.listing_title}</em></p>
                </div>
                <div className="ml-auto">
                  <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(28,28,28,0.1)', color: 'var(--gold)' }}>
                    <ShieldAlert size={11} /> Admin view
                  </span>
                </div>
              </div>

              <div ref={msgsContainerRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {hasMore && !loadingMsgs && (
                  <div className="flex justify-center pb-1">
                    <button
                      onClick={loadOlder}
                      disabled={loadingOlder}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors hover:bg-[var(--bg-subtle)]"
                      style={{ color: 'var(--text-muted)', border: '1px solid var(--border-clr)' }}
                      data-testid="admin-load-older-button"
                    >
                      {loadingOlder ? <Loader2 size={12} className="animate-spin" /> : <ChevronUp size={12} />}
                      {loadingOlder ? 'Loading…' : 'Load older messages'}
                    </button>
                  </div>
                )}
                {loadingMsgs ? (
                  <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-3/4 rounded-xl" />)}</div>
                ) : messages.map(msg => {
                  const isBuyer = msg.sender_id === activeConv.buyer_id;
                  const isAdminMsg = msg.is_admin_message;
                  return (
                    <div key={msg.id} className={`flex flex-col ${isBuyer ? 'items-start' : 'items-end'}`}>
                      <div className="flex items-center gap-1 mb-1">
                        {isAdminMsg && <ShieldAlert size={11} style={{ color: 'var(--gold)' }} />}
                        <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          {msg.sender_name}{isAdminMsg ? ' (Admin)' : ''}
                        </p>
                      </div>
                      <div
                        className="max-w-[70%] px-4 py-2.5 text-sm leading-relaxed"
                        style={{
                          backgroundColor: isAdminMsg ? 'rgba(28,28,28,0.12)' : isBuyer ? 'var(--bg-subtle)' : '#2D2D2D',
                          color: isAdminMsg ? 'var(--text)' : isBuyer ? 'var(--text)' : 'white',
                          borderRadius: isBuyer ? '18px 18px 18px 4px' : '18px 18px 4px 18px',
                          border: isAdminMsg ? '1px solid var(--gold)' : 'none',
                        }}
                      >
                        {msg.content}
                      </div>
                      <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>{formatTime(msg.timestamp)}</p>
                    </div>
                  );
                })}
                <div ref={msgsEndRef} />
              </div>

              {/* Admin input */}
              <div className="px-5 py-4 border-t" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'rgba(28,28,28,0.04)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <ShieldAlert size={13} style={{ color: 'var(--gold)' }} />
                  <span className="text-xs font-medium" style={{ color: 'var(--gold)' }}>Intervening as Admin — message visible to both parties</span>
                </div>
                <div className="flex items-end gap-3">
                  <Textarea
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type admin message… (Enter to send)"
                    rows={1}
                    className="resize-none text-sm flex-1"
                    style={{ borderColor: 'var(--gold)', backgroundColor: 'var(--surface)', maxHeight: '120px' }}
                    data-testid="admin-message-input"
                  />
                  <Button
                    onClick={sendAdminMessage}
                    disabled={sending || !draft.trim()}
                    size="icon"
                    style={{ backgroundColor: 'var(--gold)', color: 'white', borderRadius: '12px', width: '40px', height: '40px' }}
                    data-testid="admin-send-button"
                  >
                    <Send size={16} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
